import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Bell, 
  BellRing, 
  CheckCircle2, 
  AlertCircle, 
  Clock,
  AlertTriangle,
  Info,
  Filter,
  CheckCheck,
  Trash2
} from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import { useAuthorization } from '@/contexts/AuthorizationContext';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Notificacao {
  id: string;
  tarefa_id: string | null;
  vendedor_id: string;
  tipo: string;
  titulo: string;
  mensagem: string;
  lida: boolean;
  data_leitura: string | null;
  prioridade: string;
  created_at: string;
}

const getTipoIcon = (tipo: string) => {
  switch (tipo) {
    case 'lembrete':
      return <Clock className="h-4 w-4" />;
    case 'vencimento_proximo':
      return <AlertTriangle className="h-4 w-4" />;
    case 'tarefa_atrasada':
      return <AlertCircle className="h-4 w-4" />;
    case 'nova_tarefa':
      return <Bell className="h-4 w-4" />;
    case 'lead_atualizado':
      return <Info className="h-4 w-4" />;
    default:
      return <Bell className="h-4 w-4" />;
  }
};

const getTipoLabel = (tipo: string) => {
  switch (tipo) {
    case 'lembrete':
      return 'Lembrete';
    case 'vencimento_proximo':
      return 'Vencimento Próximo';
    case 'tarefa_atrasada':
      return 'Tarefa Atrasada';
    case 'nova_tarefa':
      return 'Nova Tarefa';
    case 'lead_atualizado':
      return 'Lead Atualizado';
    default:
      return tipo;
  }
};

const getPrioridadeBadge = (prioridade: string) => {
  switch (prioridade) {
    case 'urgente':
      return <Badge className="bg-red-600 text-white">Urgente</Badge>;
    case 'alta':
      return <Badge className="bg-orange-500 text-white">Alta</Badge>;
    case 'media':
      return <Badge className="bg-yellow-500 text-white">Média</Badge>;
    case 'baixa':
      return <Badge variant="secondary">Baixa</Badge>;
    default:
      return <Badge variant="secondary">{prioridade}</Badge>;
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Agora';
  if (diffMins < 60) return `${diffMins} min atrás`;
  if (diffHours < 24) return `${diffHours}h atrás`;
  if (diffDays < 7) return `${diffDays}d atrás`;
  
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function Notificacoes() {
  const { userProfile } = useAuthorization();
  const { toast } = useToast();
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterTipo, setFilterTipo] = useState<string>('all');
  const [filterPrioridade, setFilterPrioridade] = useState<string>('all');
  const [filterLida, setFilterLida] = useState<string>('all');
  const [notificacaoParaDeletar, setNotificacaoParaDeletar] = useState<string | null>(null);
  const [marcandoComoLida, setMarcandoComoLida] = useState<string | null>(null);

  const fetchNotificacoes = useCallback(async () => {
    if (!userProfile?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // A política RLS já filtra por vendedor_id = auth.uid()
      // Então não precisamos filtrar manualmente, mas podemos adicionar para clareza
      const { data, error } = await supabase
        .from('tarefas_notificacoes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        // Se houver erro de RLS, pode ser que o usuário não tenha permissão
        // ou que a política precise ser ajustada
        console.error('Erro ao buscar notificações:', error);
        throw error;
      }

      // Filtrar manualmente também para garantir (backup caso RLS não funcione)
      const filtered = (data || []).filter(n => n.vendedor_id === userProfile.id);
      setNotificacoes(filtered);
    } catch (error: any) {
      console.error('Erro ao buscar notificações:', error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível carregar as notificações.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [userProfile?.id, toast]);

  useEffect(() => {
    fetchNotificacoes();
  }, [fetchNotificacoes]);

  const marcarComoLida = async (id: string) => {
    try {
      setMarcandoComoLida(id);
      
      const { error } = await supabase
        .from('tarefas_notificacoes')
        .update({ 
          lida: true,
          data_leitura: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Notificação marcada como lida.",
      });

      await fetchNotificacoes();
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
      toast({
        title: "Erro",
        description: "Não foi possível marcar a notificação como lida.",
        variant: "destructive",
      });
    } finally {
      setMarcandoComoLida(null);
    }
  };

  const marcarTodasComoLidas = async () => {
    if (!userProfile?.id) return;

    try {
      const { error } = await supabase
        .from('tarefas_notificacoes')
        .update({ 
          lida: true,
          data_leitura: new Date().toISOString()
        })
        .eq('vendedor_id', userProfile.id)
        .eq('lida', false);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Todas as notificações foram marcadas como lidas.",
      });

      await fetchNotificacoes();
    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error);
      toast({
        title: "Erro",
        description: "Não foi possível marcar todas as notificações como lidas.",
        variant: "destructive",
      });
    }
  };

  const deletarNotificacao = async (id: string) => {
    try {
      // Primeiro, verificar se a notificação pertence ao usuário atual
      const notificacao = notificacoes.find(n => n.id === id);
      if (!notificacao) {
        throw new Error('Notificação não encontrada');
      }

      if (notificacao.vendedor_id !== userProfile?.id) {
        throw new Error('Você não tem permissão para excluir esta notificação');
      }

      const { error } = await supabase
        .from('tarefas_notificacoes')
        .delete()
        .eq('id', id)
        .eq('vendedor_id', userProfile.id); // Garantir que só deleta suas próprias notificações

      if (error) {
        console.error('Erro ao deletar notificação:', error);
        throw error;
      }

      toast({
        title: "Sucesso",
        description: "Notificação excluída.",
      });

      // Recarregar notificações do servidor para garantir sincronização
      await fetchNotificacoes();
      setNotificacaoParaDeletar(null);
    } catch (error: any) {
      console.error('Erro ao deletar notificação:', error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível excluir a notificação. Verifique se você tem permissão.",
        variant: "destructive",
      });
    }
  };

  const notificacoesFiltradas = notificacoes.filter(notif => {
    if (filterTipo !== 'all' && notif.tipo !== filterTipo) return false;
    if (filterPrioridade !== 'all' && notif.prioridade !== filterPrioridade) return false;
    if (filterLida === 'lidas' && !notif.lida) return false;
    if (filterLida === 'nao_lidas' && notif.lida) return false;
    return true;
  });

  const notificacoesNaoLidas = notificacoes.filter(n => !n.lida).length;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Notificações</h1>
          <p className="text-muted-foreground">
            Gerencie suas notificações e lembretes
          </p>
        </div>
        {notificacoesNaoLidas > 0 && (
          <Button onClick={marcarTodasComoLidas} variant="outline">
            <CheckCheck className="h-4 w-4 mr-2" />
            Marcar todas como lidas
          </Button>
        )}
      </div>

      {/* Estatísticas */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total</CardTitle>
            <Bell className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{notificacoes.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Não Lidas</CardTitle>
            <BellRing className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-500">{notificacoesNaoLidas}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Lidas</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">
              {notificacoes.length - notificacoesNaoLidas}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Tipo</label>
              <Select value={filterTipo} onValueChange={setFilterTipo}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  <SelectItem value="lembrete">Lembrete</SelectItem>
                  <SelectItem value="vencimento_proximo">Vencimento Próximo</SelectItem>
                  <SelectItem value="tarefa_atrasada">Tarefa Atrasada</SelectItem>
                  <SelectItem value="nova_tarefa">Nova Tarefa</SelectItem>
                  <SelectItem value="lead_atualizado">Lead Atualizado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Prioridade</label>
              <Select value={filterPrioridade} onValueChange={setFilterPrioridade}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas as prioridades" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as prioridades</SelectItem>
                  <SelectItem value="urgente">Urgente</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                  <SelectItem value="media">Média</SelectItem>
                  <SelectItem value="baixa">Baixa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select value={filterLida} onValueChange={setFilterLida}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="nao_lidas">Não lidas</SelectItem>
                  <SelectItem value="lidas">Lidas</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Notificações */}
      {loading ? (
        <Card>
          <CardContent className="py-10">
            <div className="text-center text-muted-foreground">Carregando notificações...</div>
          </CardContent>
        </Card>
      ) : notificacoesFiltradas.length === 0 ? (
        <Card>
          <CardContent className="py-10">
            <div className="text-center text-muted-foreground">
              {notificacoes.length === 0 
                ? "Você não tem notificações ainda."
                : "Nenhuma notificação encontrada com os filtros selecionados."}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {notificacoesFiltradas.map((notificacao) => (
            <Card 
              key={notificacao.id} 
              className={notificacao.lida ? "opacity-75" : "border-l-4 border-l-orange-500"}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      {getTipoIcon(notificacao.tipo)}
                      <h3 className={`font-semibold ${notificacao.lida ? '' : 'text-foreground'}`}>
                        {notificacao.titulo}
                      </h3>
                      {!notificacao.lida && (
                        <Badge variant="outline" className="bg-orange-500/10 text-orange-500 border-orange-500">
                          Nova
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {notificacao.mensagem}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline">
                        {getTipoLabel(notificacao.tipo)}
                      </Badge>
                      {getPrioridadeBadge(notificacao.prioridade)}
                      <span className="text-xs text-muted-foreground">
                        {formatDate(notificacao.created_at)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!notificacao.lida && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => marcarComoLida(notificacao.id)}
                        disabled={marcandoComoLida === notificacao.id}
                      >
                        <CheckCircle2 className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setNotificacaoParaDeletar(notificacao.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog de confirmação de exclusão */}
      <AlertDialog 
        open={notificacaoParaDeletar !== null} 
        onOpenChange={(open) => !open && setNotificacaoParaDeletar(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir notificação</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta notificação? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => notificacaoParaDeletar && deletarNotificacao(notificacaoParaDeletar)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
